import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isAfter, subMonths, subWeeks, subYears } from 'date-fns';

import { useGetRestrictionsPermissions } from '../../../../redux/api/users/usersApi';
import { TrainingPeriod } from '../../../../shared/constants';
import { IExercise } from '../../../../types/interfaces/exercise';
import { ITraining } from '../../../../types/interfaces/training';
import {
  ITrainingPeriodOption,
  filterTrainingPeriodOptionsByStatisticsAccess,
  getDefaultTrainingPeriodByStatisticsAccess,
} from '../../../../utils/statistics/filter-training-period-options-by-statistics-access';

interface IUseExerciseStatisticsProps {
  exercise: IExercise;
  trainings: ITraining[];
}

export function useExerciseStatistics({
  exercise,
  trainings,
}: IUseExerciseStatisticsProps) {
  const { t } = useTranslation();
  const { data: restrictionPermissions, isLoading: isRestrictionsLoading } =
    useGetRestrictionsPermissions();
  const [selectedPeriod, setSelectedPeriod] = useState<TrainingPeriod>(
    TrainingPeriod.month,
  );
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);

  const exerciseOptions = useMemo(() => {
    if (!exercise?.options || exercise.options.length === 0) return [];
    return [...exercise.options].sort(
      (a, b) => a.priorityOrder - b.priorityOrder,
    );
  }, [exercise]);

  const selectedOption = useMemo(() => {
    if (!selectedOptionId) return null;
    return exerciseOptions.find((opt) => opt.id === selectedOptionId) || null;
  }, [selectedOptionId, exerciseOptions]);

  const allPeriodOptions: ITrainingPeriodOption[] = useMemo(
    () => [
      { id: TrainingPeriod.week, name: t('statistics.period_week') },
      { id: TrainingPeriod.month, name: t('statistics.period_month') },
      { id: TrainingPeriod.threeMonth, name: t('statistics.period_3month') },
      { id: TrainingPeriod.sixMonth, name: t('statistics.period_6month') },
      { id: TrainingPeriod.oneYear, name: t('statistics.period_1year') },
      { id: TrainingPeriod.allTime, name: t('statistics.period_allTime') },
    ],
    [t],
  );

  const periodOptions = useMemo(() => {
    if (isRestrictionsLoading || !restrictionPermissions) {
      return allPeriodOptions;
    }
    return filterTrainingPeriodOptionsByStatisticsAccess(
      allPeriodOptions,
      restrictionPermissions.periodStatisticsAccess,
    );
  }, [allPeriodOptions, isRestrictionsLoading, restrictionPermissions]);

  useEffect(() => {
    const defaultPeriod =
      getDefaultTrainingPeriodByStatisticsAccess(periodOptions);
    if (!defaultPeriod) {
      return;
    }
    if (!periodOptions.some((opt) => opt.id === selectedPeriod)) {
      setSelectedPeriod(defaultPeriod);
    }
  }, [periodOptions, selectedPeriod]);

  const selectedPeriodOption = useMemo(
    () => periodOptions.find((opt) => opt.id === selectedPeriod),
    [periodOptions, selectedPeriod],
  );

  const filteredTrainings = useMemo(() => {
    if (!trainings || trainings.length === 0) return [];

    if (selectedPeriod === TrainingPeriod.allTime) {
      return trainings;
    }

    const now = new Date();
    let cutoffDate: Date;

    switch (selectedPeriod) {
      case TrainingPeriod.week:
        cutoffDate = subWeeks(now, 1);
        break;
      case TrainingPeriod.month:
        cutoffDate = subMonths(now, 1);
        break;
      case TrainingPeriod.threeMonth:
        cutoffDate = subMonths(now, 3);
        break;
      case TrainingPeriod.sixMonth:
        cutoffDate = subMonths(now, 6);
        break;
      case TrainingPeriod.oneYear:
        cutoffDate = subYears(now, 1);
        break;
      default:
        return trainings;
    }

    return trainings.filter((training) => {
      if (!training.startDate) return false;
      const trainingDate = new Date(training.startDate);
      return isAfter(trainingDate, cutoffDate);
    });
  }, [trainings, selectedPeriod]);

  const handlePeriodChange = (e: any) => {
    const value = e?.target?.value;
    if (value?.id) {
      setSelectedPeriod(value.id as TrainingPeriod);
    }
  };

  const handleOptionChange = (e: any) => {
    const value = e?.target?.value;
    if (value?.id) {
      setSelectedOptionId(value.id);
    }
  };

  useEffect(() => {
    if (!selectedOptionId && exerciseOptions.length > 0) {
      setSelectedOptionId(exerciseOptions[0].id);
    }
  }, [selectedOptionId, exerciseOptions]);

  return {
    selectedPeriod,
    selectedPeriodOption,
    periodOptions,
    filteredTrainings,
    isLoading: false,
    handlePeriodChange,
    exerciseOptions,
    selectedOption,
    selectedOptionId,
    handleOptionChange,
  };
}
